import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { Box, CircularProgress, Divider, Tabs, Tab, IconButton, Tooltip, Button } from '@mui/material';
import {
  Security,
  PlayCircleOutline,
  History as HistoryIcon,
  SmartToy,
  ChevronLeft,
  ChevronRight,
  Delete as DeleteIcon,
  DriveFileMove as MoveIcon,
} from '@mui/icons-material';
import TabPanel from '../../pages/Question/QuestionDetail/components/TabPanel';
import AIChatSidebar from './AIChatSidebar';
import QuestionHeader from '../../pages/Question/QuestionDetail/components/QuestionHeader';
import CodeEditor from '../../pages/Question/QuestionDetail/components/CodeWorkspace/CodeEditor';
import OutputPanel from '../../pages/Question/QuestionDetail/components/CodeWorkspace/OutputPanel';
import TestResultPanel from '../../pages/Question/QuestionDetail/components/CodeWorkspace/TestResultPanel';
import PublicTestEditor from '../../pages/Question/QuestionDetail/components/TestCasesPanel/PublicTestEditor';
import SecretTestEditor from '../../pages/Question/QuestionDetail/components/TestCasesPanel/SecretTestEditor';
import SubmissionsPanel from '../../pages/Question/QuestionDetail/components/SubmissionsPanel/SubmissionsPanel';
import MarkdownRenderer from '../Common/MarkdownRenderer';
import EditDialog from './EditDialog';
import { Edit as EditIcon, PlayArrow, Assessment as ResultIcon } from '@mui/icons-material';
import { useCodeExecution, useSubmissions } from '../../pages/Question/QuestionDetail/hooks';
import axios from '../../api/axios';
import ExerciseNavigation from './ExerciseNavigation';

/**
 * Notebook代码题组件
 * 使用和QuestionDetail完全一样的UI结构
 */
function NotebookCodeQuestion({ example, onUpdate, sectionExamples = [], sectionIndex = -1, onPrevious, onNext, onDelete, onMoveToSection }) {
  const { exampleId } = useParams();
  const { user } = useContext(AuthContext);
  const isTeacher = user?.role === 'teacher';
  
  const questionId = `notebook_${exampleId}`;
  
  // 代码区显示可编辑的骨架：优先用 codeAnswer（纯骨架），没有再用 question 里提取
  const getTemplateCode = () => {
    if (!example) return '';

    // 优先使用 codeAnswer：约定为「仅可编译的骨架」，供学生在 Code 标签里编辑
    if (example.codeAnswer && example.codeAnswer.trim()) {
      const codeBlockMatch = example.codeAnswer.match(/```python\n?([\s\S]*?)```/);
      if (codeBlockMatch && codeBlockMatch[1].trim()) {
        return codeBlockMatch[1].trim();
      }
      return example.codeAnswer.replace(/```python\n?/g, '').replace(/```/g, '').trim();
    }

    // 兼容旧题：没有 codeAnswer 时再从 question 里提取代码块
    if (example.question) {
      const codeBlockMatch = example.question.match(/```python\n?([\s\S]*?)```/);
      if (codeBlockMatch && codeBlockMatch[1].trim()) {
        return codeBlockMatch[1].trim();
      }
      if (example.question.includes('def ') || example.question.includes('class ')) {
        return example.question.replace(/```python\n?/g, '').replace(/```/g, '').trim();
      }
    }

    return '';
  };

  const question = example ? {
    id: questionId,
    title: example.question?.substring(0, 50) || '代码题',
    description: '',
    category: example.sectionTitle || 'Notebook',
    difficulty: 'medium',
    language: 'python',
    templateCode: getTemplateCode(),
    publicTestCode: example.publicTests || '',
    secretTestCode: example.secretTests || '',
  } : null;

  // Hooks
  const { submissions, refetch: refetchSubmissions, addSubmission } = useSubmissions(questionId);
  const {
    running,
    executing,
    submitting,
    runResult,
    executeResult,
    result,
    runCode,
    executeCode,
    submitCode
  } = useCodeExecution(question, questionId);

  // Local state
  const [code, setCode] = useState('');
  const [leftTab, setLeftTab] = useState(0); // 0: Description, 1: Code, 2: Output, 3: Test Result
  const [rightTab, setRightTab] = useState(0);
  const [unifiedTab, setUnifiedTab] = useState(0); // Used when right panel is collapsed
  const [timerState, setTimerState] = useState({ running: false, elapsedTime: 0 });
  const [editingPublicTest, setEditingPublicTest] = useState(false);
  const [editingSecretTest, setEditingSecretTest] = useState(false);
  const [publicTestCode, setPublicTestCode] = useState('');
  const [secretTestCode, setSecretTestCode] = useState('');
  const [savingTest, setSavingTest] = useState(false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingExample, setEditingExample] = useState(null);
  const [savingExample, setSavingExample] = useState(false);

  // Initialize code when example loads
  useEffect(() => {
    if (example) {
      // Try to get latest submission code first
      const initCode = async () => {
        try {
          const response = await axios.get(`/submissions/question/${questionId}`);
          if (response.data && response.data.length > 0 && response.data[0].code) {
            // Check if code is JSON (for other question types) or actual code
            let isCode = true;
            try {
              const parsed = JSON.parse(response.data[0].code);
              if (typeof parsed === 'object' && !Array.isArray(parsed)) {
                isCode = false;
              }
            } catch (e) {
              isCode = true;
            }
            if (isCode) {
              setCode(response.data[0].code);
              return;
            }
          }
        } catch (error) {
          console.error('Error fetching submissions:', error);
        }
        
        // If no submission, use template code from getTemplateCode() function
        // This handles both cases: question contains code template, or just description text
        const templateCode = getTemplateCode();
        if (templateCode) {
          setCode(templateCode);
        } else {
          // If still no code, set a basic placeholder
          setCode('# 请在此处编写你的代码\npass');
        }
      };
      
      initCode();
      setPublicTestCode(example.publicTests || '');
      setSecretTestCode(example.secretTests || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [example, questionId]);

  // Auto-switch to appropriate tab when results arrive
  useEffect(() => {
    if (executeResult && !rightPanelCollapsed) {
      setLeftTab(2); // Switch to Output tab
    } else if (executeResult && rightPanelCollapsed) {
      setUnifiedTab(2); // Switch to Output tab in unified mode
    }
  }, [executeResult, rightPanelCollapsed]);

  useEffect(() => {
    if (runResult && !rightPanelCollapsed) {
      setLeftTab(3); // Switch to Test Result tab
    } else if (runResult && rightPanelCollapsed) {
      setUnifiedTab(3); // Switch to Test Result tab in unified mode
    }
  }, [runResult, rightPanelCollapsed]);

  // Handle reset code wrapper
  const handleResetCodeWrapper = () => {
    if (question && window.confirm('确定要重置代码到模板吗？当前代码将会丢失。')) {
      handleResetCode(question.templateCode);
    }
  };

  // Calculate unified tab indices based on user role
  // Unified tabs when collapsed: [Description (0), Code (1), Output (2), Test Result (3), Public Test (4), Secret Test (5, teacher only), Submissions (6/5)]
  const getUnifiedSubmissionsTabIndex = () => (isTeacher ? 6 : 5);

  // Handle code execution
  const handleExecute = async () => {
    try {
      await executeCode(code);
      setRightTab(isTeacher ? 2 : 1);
    } catch (error) {
      // Error handled by hook
    }
  };

  // Handle code submission
  const handleSubmit = async () => {
    try {
      const submission = await submitCode(code);
      addSubmission(submission);
      refetchSubmissions();
      setRightTab(getSubmissionsTabIndex());
      if (timerState.running) {
        setTimerState({ ...timerState, running: false });
      }
    } catch (error) {
      // Error handled by hook
    }
  };

  // Handle running with test cases
  const handleRun = async (testType = 'public') => {
    try {
      await runCode(code, testType);
    } catch (error) {
      // Error handled by hook
    }
  };

  // Handle test cases editing
  const handleEditPublicTest = () => {
    setEditingPublicTest(true);
  };

  const handleCancelPublicTest = () => {
    setEditingPublicTest(false);
    setPublicTestCode(example?.publicTests || '');
  };

  const handleEditSecretTest = () => {
    setEditingSecretTest(true);
  };

  const handleCancelSecretTest = () => {
    setEditingSecretTest(false);
    setSecretTestCode(example?.secretTests || '');
  };

  // Handle editing the entire question
  const handleEditQuestion = () => {
    if (!example) return;
    setEditingExample({ ...example });
    setEditDialogOpen(true);
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingExample(null);
  };

  const handleSaveQuestion = async () => {
    if (!editingExample || !exampleId) return;

    setSavingExample(true);
    try {
      await axios.put(`/notebook-examples/${exampleId}`, editingExample);
      setEditDialogOpen(false);
      setEditingExample(null);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error saving question:', error);
      alert('保存失败：' + (error.response?.data?.error || '请重试'));
    } finally {
      setSavingExample(false);
    }
  };

  const handleSavePublicTest = async () => {
    if (!isTeacher || !exampleId) return;

    setSavingTest(true);
    try {
      await axios.put(`/notebook-examples/${exampleId}`, {
        publicTests: publicTestCode
      });
      setEditingPublicTest(false);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error saving public test:', error);
      alert('保存失败：' + (error.response?.data?.error || '请重试'));
    } finally {
      setSavingTest(false);
    }
  };

  const handleSaveSecretTest = async () => {
    if (!isTeacher || !exampleId) return;

    setSavingTest(true);
    try {
      await axios.put(`/notebook-examples/${exampleId}`, {
        secretTests: secretTestCode
      });
      setEditingSecretTest(false);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error saving secret test:', error);
      alert('保存失败：' + (error.response?.data?.error || '请重试'));
    } finally {
      setSavingTest(false);
    }
  };

  // Handle reset code
  const handleResetCode = (templateCode) => {
    setCode(templateCode);
  };

  // Calculate right tab indices based on user role
  // Teacher: [Public Test (0), Secret Test (1), Submissions (2)]
  // Student: [Public Test (0), Submissions (1)]
  const getSubmissionsTabIndex = () => (isTeacher ? 2 : 1);

  if (!example || !question) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  const editorLanguage = 'python';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        bgcolor: 'background.default',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      {/* Header - 右侧从左到右：Timer、收藏、AI 助手、删除、头像（头像始终最右） */}
      <QuestionHeader
        question={question}
        user={user}
        executing={executing}
        submitting={submitting}
        onExecute={handleExecute}
        onSubmit={handleSubmit}
        onTimerChange={setTimerState}
        hideTitle={true}
        rightActions={
          <>
            {example?.enableAI !== false && (
              <Tooltip title={aiChatOpen ? '关闭AI助手' : '打开AI助手'}>
                <IconButton
                  size="small"
                  onClick={() => setAiChatOpen(!aiChatOpen)}
                  color={aiChatOpen ? 'primary' : 'default'}
                  sx={{
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <SmartToy />
                </IconButton>
              </Tooltip>
            )}
            {isTeacher && onMoveToSection && (
              <Tooltip title="移动到章节">
                <IconButton
                  size="small"
                  onClick={onMoveToSection}
                  color="default"
                  sx={{
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <MoveIcon />
                </IconButton>
              </Tooltip>
            )}
            {isTeacher && onDelete && (
              <Tooltip title="删除练习题">
                <IconButton
                  size="small"
                  onClick={onDelete}
                  color="error"
                  sx={{
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </>
        }
      />

      <Divider />

      {/* Main Content Area */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden', flexDirection: 'column', position: 'relative' }}>
        {/* Main Layout - Left and Right Panels */}
        <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden', flexDirection: 'row' }}>
          {/* Left Side - Code Workspace (with unified tabs when collapsed) */}
          <Box sx={{ 
            width: rightPanelCollapsed ? '100%' : '50%', 
            display: 'flex', 
            flexDirection: 'column', 
            borderRight: rightPanelCollapsed ? 0 : 1, 
            borderColor: 'divider',
            flex: rightPanelCollapsed ? 1 : '0 0 50%',
            minWidth: 0
          }}>
            {/* Left Side Tabs (or Unified Tabs when collapsed) */}
            <Box sx={{ 
              borderBottom: 1, 
              borderColor: 'divider', 
              bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.background.paper : 'grey.50',
              height: 48, 
              display: 'flex', 
              alignItems: 'flex-end', 
              position: 'relative',
              minWidth: 0
            }}>
              {/* When collapsed, show expand button on LEFT so it doesn't cover Public Test / Secret / Submissions */}
              {rightPanelCollapsed && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, ml: 0.5, mr: 0.5, height: 48 }}>
                  <Tooltip title="展开右侧面板">
                    <IconButton
                      size="small"
                      onClick={() => setRightPanelCollapsed(false)}
                    >
                      <ChevronRight />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              <Tabs 
                value={Math.min(
                  rightPanelCollapsed ? unifiedTab : leftTab,
                  rightPanelCollapsed ? (isTeacher ? 6 : 5) : 3
                )} 
                onChange={(e, v) => {
                  const index = typeof v === 'number' ? v : Number(v);
                  if (rightPanelCollapsed) {
                    setUnifiedTab(index);
                  } else {
                    setLeftTab(index);
                  }
                }}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ 
                  minHeight: 48, 
                  height: 48, 
                  flexGrow: 1, 
                  minWidth: 0,
                  '& .MuiTabs-flexContainer': { minHeight: 48 },
                  '& .MuiTabs-scroller': { overflow: 'auto !important' }
                }}
              >
                <Tab
                  label="Description"
                  sx={{ minHeight: 48 }}
                />
                <Tab
                  label="Code"
                  icon={<EditIcon fontSize="small" />}
                  iconPosition="start"
                  sx={{ minHeight: 48 }}
                />
                <Tab
                  label="Output"
                  icon={<PlayArrow fontSize="small" />}
                  iconPosition="start"
                  sx={{ minHeight: 48 }}
                />
                <Tab
                  label="Test Result"
                  icon={<ResultIcon fontSize="small" />}
                  iconPosition="start"
                  sx={{ minHeight: 48 }}
                />
                {/* When collapsed, add right-side tabs as direct children (no Fragment) so MUI index is correct */}
                {rightPanelCollapsed && (
                  <Tab
                    label="Public Test"
                    icon={<PlayCircleOutline fontSize="small" />}
                    iconPosition="start"
                    sx={{ minHeight: 48 }}
                  />
                )}
                {rightPanelCollapsed && isTeacher && (
                  <Tab
                    label="Secret Test"
                    icon={<Security fontSize="small" />}
                    iconPosition="start"
                    sx={{ minHeight: 48 }}
                  />
                )}
                {rightPanelCollapsed && (
                  <Tab
                    label="Submissions"
                    icon={<HistoryIcon fontSize="small" />}
                    iconPosition="start"
                    sx={{ minHeight: 48 }}
                  />
                )}
              </Tabs>
              {/* EditorToolbar - show when on Code tab */}
              {((rightPanelCollapsed && unifiedTab === 1) || (!rightPanelCollapsed && leftTab === 1)) && (
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, mr: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', px: 2 }}>
                    <Button
                      size="small"
                      variant="text"
                      onClick={handleResetCodeWrapper}
                      disabled={!question}
                      sx={{ 
                        fontSize: '0.75rem',
                        color: 'text.primary'
                      }}
                    >
                      Reset to Default
                    </Button>
                  </Box>
                </Box>
              )}
              {/* Collapse button - only when NOT collapsed (expand button when collapsed is on the left) */}
              {!rightPanelCollapsed && (
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, mr: 1, mb: 0.5 }}>
                  <Tooltip title="收起右侧面板">
                    <IconButton
                      size="small"
                      onClick={() => setRightPanelCollapsed(true)}
                    >
                      <ChevronLeft />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>

            {/* Tab Panels - Left Side (Description, Code, Output, Test Result) */}
            {!rightPanelCollapsed && (
              <>
                <TabPanel value={leftTab} index={0} sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0, p: 2 }}>
                  {example?.question ? (
                    <MarkdownRenderer content={example.question} />
                  ) : (
                    <Box sx={{ color: 'text.secondary' }}>暂无题目描述。</Box>
                  )}
                </TabPanel>
                <TabPanel value={leftTab} index={1} sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
                  <CodeEditor
                    language={editorLanguage}
                    value={code}
                    onChange={setCode}
                    readOnly={false}
                  />
                </TabPanel>
                <TabPanel value={leftTab} index={2} sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
                  <OutputPanel executeResult={executeResult} />
                </TabPanel>
                <TabPanel value={leftTab} index={3} sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
                  <TestResultPanel runResult={runResult} userRole={user?.role || 'student'} />
                </TabPanel>
              </>
            )}

            {/* Unified Tab Panels - When Collapsed */}
            {rightPanelCollapsed && (
              <>
                {/* Description Tab */}
                <TabPanel value={unifiedTab} index={0} sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0, p: 2 }}>
                  {example?.question ? (
                    <MarkdownRenderer content={example.question} />
                  ) : (
                    <Box sx={{ color: 'text.secondary' }}>暂无题目描述。</Box>
                  )}
                </TabPanel>
                {/* Code Tab */}
                <TabPanel value={unifiedTab} index={1} sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
                  <CodeEditor
                    language={editorLanguage}
                    value={code}
                    onChange={setCode}
                    readOnly={false}
                  />
                </TabPanel>
                {/* Output Tab */}
                <TabPanel value={unifiedTab} index={2} sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
                  <OutputPanel executeResult={executeResult} />
                </TabPanel>
                {/* Test Result Tab */}
                <TabPanel value={unifiedTab} index={3} sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
                  <TestResultPanel runResult={runResult} userRole={user?.role || 'student'} />
                </TabPanel>
                {/* Public Test Tab */}
                <TabPanel value={unifiedTab} index={4} sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
                  <PublicTestEditor
                    question={question}
                    testCode={publicTestCode}
                    onTestCodeChange={setPublicTestCode}
                    editing={editingPublicTest}
                    onEdit={handleEditPublicTest}
                    onSave={handleSavePublicTest}
                    onCancel={handleCancelPublicTest}
                    onRun={handleRun}
                    running={running}
                    saving={savingTest}
                    isTeacher={isTeacher}
                  />
                </TabPanel>
                {/* Secret Test Tab - Teacher Only */}
                {isTeacher && (
                  <TabPanel value={unifiedTab} index={5} sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
                    <SecretTestEditor
                      question={question}
                      testCode={secretTestCode}
                      onTestCodeChange={setSecretTestCode}
                      editing={editingSecretTest}
                      onEdit={handleEditSecretTest}
                      onSave={handleSaveSecretTest}
                      onCancel={handleCancelSecretTest}
                      onRun={handleRun}
                      running={running}
                      saving={savingTest}
                    />
                  </TabPanel>
                )}
                {/* Submissions Tab */}
                <TabPanel value={unifiedTab} index={getUnifiedSubmissionsTabIndex()} sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
                  <SubmissionsPanel submissions={submissions} latestResult={result} />
                </TabPanel>
              </>
            )}
          </Box>

          {/* Right Side - Tabs (when not collapsed) */}
          {!rightPanelCollapsed && (
            <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper', flexShrink: 0 }}>
              {/* Right Side Tabs */}
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.background.paper : 'grey.50',
                  height: 48,
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'space-between'
                }}
              >
                <Tabs value={rightTab} onChange={(e, v) => setRightTab(v)} sx={{ minHeight: 48, height: 48 }}>
                  <Tab
                    label="Public Test"
                    icon={<PlayCircleOutline fontSize="small" />}
                    iconPosition="start"
                    sx={{ minHeight: 48 }}
                  />
                  {isTeacher && (
                    <Tab
                      label="Secret Test"
                      icon={<Security fontSize="small" />}
                      iconPosition="start"
                      sx={{ minHeight: 48 }}
                    />
                  )}
                  <Tab
                    label="Submissions"
                    icon={<HistoryIcon fontSize="small" />}
                    iconPosition="start"
                    sx={{ minHeight: 48 }}
                  />
                </Tabs>
              </Box>

              {/* Public Test Tab */}
              <TabPanel value={rightTab} index={0} sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
                <PublicTestEditor
                  question={question}
                  testCode={publicTestCode}
                  onTestCodeChange={setPublicTestCode}
                  editing={editingPublicTest}
                  onEdit={isTeacher ? handleEditQuestion : handleEditPublicTest}
                  onSave={handleSavePublicTest}
                  onCancel={handleCancelPublicTest}
                  onRun={handleRun}
                  running={running}
                  saving={savingTest}
                  isTeacher={isTeacher}
                />
              </TabPanel>

              {/* Secret Test Tab - Teacher Only */}
              {isTeacher && (
                <TabPanel value={rightTab} index={1} sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
                  <SecretTestEditor
                    question={question}
                    testCode={secretTestCode}
                    onTestCodeChange={setSecretTestCode}
                    editing={editingSecretTest}
                    onEdit={handleEditSecretTest}
                    onSave={handleSaveSecretTest}
                    onCancel={handleCancelSecretTest}
                    onRun={handleRun}
                    running={running}
                    saving={savingTest}
                  />
                </TabPanel>
              )}

              {/* Submissions Tab */}
              <TabPanel value={rightTab} index={getSubmissionsTabIndex()} sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
                <SubmissionsPanel submissions={submissions} latestResult={result} />
              </TabPanel>
            </Box>
          )}
        </Box>
      </Box>

      {/* AI Chat Sidebar */}
      {example?.enableAI !== false && aiChatOpen && (
        <AIChatSidebar
          open={aiChatOpen}
          exampleId={exampleId}
          question={example?.question || ''}
          questionType={example?.questionType || 'code'}
          studentAnswer={code}
          onClose={() => setAiChatOpen(false)}
        />
      )}

      {/* Navigation Bar - 上一题/下一题 */}
      {sectionExamples.length > 0 && (
        <ExerciseNavigation
          sectionExamples={sectionExamples}
          sectionIndex={sectionIndex}
          onPrevious={onPrevious}
          onNext={onNext}
        />
      )}

      {/* Edit Dialog */}
      {isTeacher && (
        <EditDialog
          open={editDialogOpen}
          example={example}
          editingExample={editingExample}
          setEditingExample={setEditingExample}
          saving={savingExample}
          onSave={handleSaveQuestion}
          onCancel={handleCancelEdit}
        />
      )}
    </Box>
  );
}

export default NotebookCodeQuestion;

